import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  deleteDoc
} from '@angular/fire/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { environment } from '../../environments/environment';
import { User, UserRole } from '../models/user.model';

const TEST_USERS: Record<string, { role: UserRole; departmentIds?: string[] }> = {
  'admin@email.com':              { role: UserRole.ADMIN },
  'minister@nimbletech.co.in':    { role: UserRole.MINISTER },
  'osd@email.com':                { role: UserRole.OSD },
  'test_op@email.com':            { role: UserRole.OPERATOR, departmentIds: ['works'] },
  'test_ap@email.com':            { role: UserRole.APPROVER, departmentIds: ['works'] }
};

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private functions = inject(Functions);
  private injector  = inject(Injector);
  private usersCollection = collection(this.firestore, 'users');

  private run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  /** Normalize emails for Auth + Firestore lookups. */
  normalizeEmail(email: string | null | undefined): string {
    return String(email ?? '').trim().toLowerCase();
  }

  /**
   * Pass-through role reader. Minister and OSD are now distinct roles.
   *
   * One-time correction: an earlier release (Aug 2026) auto-migrated
   * `minister@nimbletech.co.in` from `'minister'` → `'osd'`. If we read that
   * doc back and the email is still the seeded minister account, we restore
   * `'minister'` so the UI shows the correct identity.
   */
  private normalizeRole(rawRole: any, uid?: string, email?: string): UserRole {
    if (
      uid &&
      email === 'minister@nimbletech.co.in' &&
      rawRole === 'osd'
    ) {
      this.run(() => updateDoc(doc(this.usersCollection, uid), { role: 'minister' }))
        .catch(err => console.warn('[users] minister role restoration failed:', err));
      return UserRole.MINISTER;
    }
    return rawRole as UserRole;
  }

  private resolveDepartmentIds(data: Record<string, unknown>): string[] {
    const fromDoc = (data['departmentIds'] as string[] | undefined) || [];
    if (fromDoc.length > 0) return fromDoc;
    const email = String(data['email'] || '');
    const testIds = email ? TEST_USERS[email]?.departmentIds : undefined;
    return testIds ? [...testIds] : [];
  }

  /**
   * Get user data by UID
   */
  async getUserByUid(uid: string): Promise<User | null> {
    const userSnapshot = await this.run(() => getDoc(doc(this.usersCollection, uid)));
    if (!userSnapshot.exists()) return null;
    return this.mapUserDoc(userSnapshot);
  }

  /**
   * Get user data by email
   */
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const trimmed = String(email ?? '').trim();
      const normalized = this.normalizeEmail(trimmed);
      const candidates = [...new Set([normalized, trimmed].filter(Boolean))];

      for (const candidate of candidates) {
        const querySnapshot = await this.run(() =>
          getDocs(query(this.usersCollection, where('email', '==', candidate)))
        );
        if (!querySnapshot.empty) {
          return this.mapUserDoc(querySnapshot.docs[0]);
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching user by email:', error);
      throw error;
    }
  }

  private mapUserDoc(userDoc: { id: string; data: () => Record<string, unknown> }): User {
    const data = userDoc.data();
    return {
      uid: userDoc.id,
      email: data['email'] as string,
      displayName: (data['displayName'] ?? data['fullName'] ?? '') as string,
      fullName: (data['fullName'] ?? data['displayName']) as string | undefined,
      designation: data['designation'] as string | undefined,
      profileImage: (data['profileImage'] || data['photoURL'] || data['avatarUrl']) as string | undefined,
      mobileNumber: (data['mobileNumber'] || data['phoneNumber']) as string | undefined,
      employeeId: data['employeeId'] as string | undefined,
      role: this.normalizeRole(data['role'], userDoc.id, data['email'] as string | undefined),
      departmentIds: this.resolveDepartmentIds(data),
      blockAccess: (data['blockAccess'] as string[] | undefined) || [],
      createdAt: (data['createdAt'] as { toDate?: () => Date })?.toDate?.() || new Date(),
      lastLogin: (data['lastLogin'] as { toDate?: () => Date })?.toDate?.(),
      isActive: data['isActive'] !== false,
      remarks: data['remarks'] as string | undefined,
      assignedBy: data['assignedBy'] as string | undefined,
      lastModifiedBy: data['lastModifiedBy'] as string | undefined,
      assignmentAudit: (data['assignmentAudit'] as User['assignmentAudit']) || [],
      assignedDepartments: (data['assignedDepartments'] as string[]) || [],
      assignedSchemes: (data['assignedSchemes'] as string[]) || [],
      assignedApproverIds: (data['assignedApproverIds'] as string[]) || [],
      assignedOperators: (data['assignedOperators'] as string[]) || [],
      approvalLevel: data['approvalLevel'] as string | undefined,
      approvalScope: data['approvalScope'] as string | undefined,
      permissions: (data['permissions'] as Record<string, boolean>) || {},
    };
  }

  /**
   * Copy the canonical profile to users/{authUid} so security rules and login
   * always resolve against the Firebase Auth UID (fixes legacy doc-id mismatches).
   */
  async mirrorProfileToAuthUid(authUid: string, source: User): Promise<User> {
    const email = this.normalizeEmail(source.email);
    const payload: Record<string, unknown> = {
      uid: authUid,
      email,
      displayName: source.displayName,
      fullName: source.fullName ?? source.displayName ?? null,
      mobileNumber: source.mobileNumber ?? null,
      employeeId: source.employeeId ?? null,
      role: source.role,
      departmentIds: source.departmentIds ?? [],
      blockAccess: source.blockAccess ?? [],
      assignedDepartments: source.assignedDepartments ?? source.departmentIds ?? [],
      assignedSchemes: source.assignedSchemes ?? [],
      assignedApproverIds: source.assignedApproverIds ?? [],
      assignedOperators: source.assignedOperators ?? [],
      approvalLevel: source.approvalLevel ?? null,
      approvalScope: source.approvalScope ?? null,
      permissions: source.permissions ?? {},
      isActive: source.isActive !== false,
      remarks: source.remarks ?? null,
      assignedBy: source.assignedBy ?? null,
      lastModifiedBy: source.lastModifiedBy ?? null,
      updatedAt: serverTimestamp(),
    };
    if (Array.isArray(source.assignmentAudit)) {
      payload['assignmentAudit'] = source.assignmentAudit;
    }
    await this.run(() => setDoc(doc(this.usersCollection, authUid), payload, { merge: true }));

    // Clean up the stale doc at the old id so mirroring never leaves a duplicate profile behind.
    if (source.uid && source.uid !== authUid) {
      try {
        await this.run(() => deleteDoc(doc(this.usersCollection, source.uid)));
      } catch (err) {
        console.warn('[users] failed to delete stale profile after mirroring:', err);
      }
    }

    return { ...source, uid: authUid, email, isActive: source.isActive !== false };
  }

  /**
   * Ensure a minimal document exists at users/{authUid} so Firestore security
   * rules (which look up users/{request.auth.uid}) can always resolve the role.
   * Called when the profile was found by email but its doc ID ≠ Firebase Auth UID.
   */
  async ensureAuthUidBridge(authUid: string, role: UserRole, email: string): Promise<void> {
    const ref = doc(this.usersCollection, authUid);
    const snap = await this.run(() => getDoc(ref));
    if (!snap.exists()) {
      await this.run(() =>
        setDoc(ref, { uid: authUid, role, email, isActive: true, createdAt: serverTimestamp() }, { merge: true })
      );
    } else if (snap.data()['role'] !== role) {
      await this.run(() => updateDoc(ref, { role }));
    }
  }

  /** Auto-provision a Firestore profile for a Firebase Auth user on first login. */
  async createSelfProfile(firebaseUser: { uid: string; email: string | null; displayName: string | null }): Promise<User> {
    const displayName = firebaseUser.email === 'minister@nimbletech.co.in'
      ? 'Sri Suryabanshi Suraj'
      : (firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User');

    // Default role is ADMIN; override if the email matches a test user
    const testConfig = firebaseUser.email
      ? TEST_USERS[this.normalizeEmail(firebaseUser.email)] ?? TEST_USERS[firebaseUser.email]
      : undefined;
    const role = testConfig?.role ?? UserRole.OPERATOR;
    const departmentIds = testConfig?.departmentIds ?? [];

    const userData: any = {
      uid: firebaseUser.uid,
      email: this.normalizeEmail(firebaseUser.email || ''),
      displayName,
      role,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      isActive: true,
      departmentIds,
      assignedDepartments: [],
      assignedSchemes: [],
      assignedApproverIds: [],
      assignedOperators: [],
      permissions: {}
    };
    await this.run(() => setDoc(doc(this.usersCollection, firebaseUser.uid), userData));
    return { ...userData, uid: firebaseUser.uid, createdAt: new Date(), lastLogin: new Date() };
  }

  /**
   * Creates a Firebase Auth user via a secondary app instance so the
   * current admin session is not disrupted. Returns the new user's UID.
   */
  async createFirebaseAuthUser(email: string, password: string): Promise<string> {
    const normalizedEmail = this.normalizeEmail(email);
    const pwd = String(password ?? '');
    if (!normalizedEmail) {
      throw Object.assign(new Error('Email is required.'), { code: 'auth/missing-email' });
    }
    if (pwd.length < 6) {
      throw Object.assign(new Error('Password must be at least 6 characters.'), { code: 'auth/weak-password' });
    }

    const appName = `TempAuth_${Date.now()}`;
    const secondaryApp = initializeApp(environment.firebase, appName);
    const secondaryAuth = getAuth(secondaryApp);
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, normalizedEmail, pwd);
      await fbSignOut(secondaryAuth);
      return cred.user.uid;
    } finally {
      await deleteApp(secondaryApp);
    }
  }

  /** Whether Firebase Auth already has this email (even if Firestore profile is missing). */
  async isEmailRegisteredInAuth(email: string): Promise<boolean> {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) return false;
    try {
      const methods = await fetchSignInMethodsForEmail(this.auth, normalizedEmail);
      return methods.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Create Auth account for admin user registration, or recover UID when Auth exists
   * but Firestore profile was never written (e.g. prior failed save).
   */
  async obtainAuthUidForRegistration(email: string, password: string): Promise<string> {
    const normalizedEmail = email.trim();
    const pwd = String(password ?? '');

    try {
      return await this.createFirebaseAuthUser(normalizedEmail, pwd);
    } catch (error: any) {
      if (error?.code !== 'auth/email-already-in-use') {
        throw this.mapAuthError(error);
      }

      const existingProfile = await this.getUserByEmail(normalizedEmail);
      if (existingProfile) {
        throw new Error('A user with this email already exists in the system.');
      }

      try {
        return await this.resolveAuthUidForExistingAccount(normalizedEmail, pwd);
      } catch (signInErr: any) {
        const code = signInErr?.code ?? '';
        if (code === 'auth/wrong-password' || code === 'auth/invalid-credential' || code === 'auth/invalid-login-credentials') {
          throw new Error(
            'This email is already registered with a different password. Use another email or remove the account in Firebase Console → Authentication.'
          );
        }
        throw this.mapAuthError(signInErr);
      }
    }
  }

  private mapAuthError(error: any): Error {
    const code = error?.code ?? '';
    const messages: Record<string, string> = {
      'auth/email-already-in-use': 'This email is already registered.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/weak-password': 'Password must be at least 6 characters.',
      'auth/operation-not-allowed': 'Email/password sign-up is disabled in Firebase. Enable it under Authentication → Sign-in method.',
      'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
    };
    const message = messages[code] || error?.message || 'Could not create the authentication account.';
    return Object.assign(new Error(message), { code });
  }

  /**
   * Admin-only: set Firebase Auth password.
   * Tries dev server → Cloud Function → create Auth if missing → reset email.
   * Returns how the password was applied.
   */
  async adminResetUserPassword(
    uid: string,
    email: string,
    password: string,
  ): Promise<'set' | 'email-sent'> {
    if (!uid) throw new Error('User id is required to reset password.');
    const pwd = String(password ?? '');
    if (pwd.length < 6) {
      throw new Error('Password must be at least 6 characters.');
    }
    const normalizedEmail = this.normalizeEmail(email);

    if (!environment.production && (environment as { adminResetPasswordUrl?: string }).adminResetPasswordUrl) {
      try {
        await this.adminResetPasswordViaDevServer(uid, pwd);
        return 'set';
      } catch (err) {
        console.warn('[password reset] Dev server unavailable:', err);
      }
    }

    try {
      await this.adminResetPasswordViaCallable(uid, pwd);
      return 'set';
    } catch (err) {
      console.warn('[password reset] Cloud Function unavailable:', err);
    }

    try {
      await this.ensureAuthAccountWithPassword(normalizedEmail, pwd, uid);
      return 'set';
    } catch (err: any) {
      if (err?.message !== 'auth-account-exists') {
        throw err;
      }
    }

    await sendPasswordResetEmail(this.auth, normalizedEmail);
    return 'email-sent';
  }

  /** Create Firebase Auth when profile exists but Auth account is missing. */
  private async ensureAuthAccountWithPassword(
    email: string,
    password: string,
    profileUid: string,
  ): Promise<void> {
    const methods = await fetchSignInMethodsForEmail(this.auth, email);
    if (methods.length > 0) {
      throw new Error('auth-account-exists');
    }

    const authUid = await this.createFirebaseAuthUser(email, password);
    if (authUid !== profileUid) {
      const profile =
        (await this.getUserByUid(profileUid)) ??
        (await this.getUserByEmail(email));
      if (profile) {
        await this.mirrorProfileToAuthUid(authUid, profile);
      }
    }
  }

  private async adminResetPasswordViaDevServer(uid: string, password: string): Promise<void> {
    const url = (environment as { adminResetPasswordUrl?: string }).adminResetPasswordUrl;
    if (!url) throw new Error('Dev reset server URL not configured.');

    const user = this.auth.currentUser;
    if (!user) throw new Error('You must be logged in as admin to reset passwords.');
    const token = await user.getIdToken();

    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ uid, password }),
      });
    } catch {
      throw new Error('dev-server-unavailable');
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.error || 'Could not reset password.';
      if (res.status === 503) {
        throw new Error('dev-server-unavailable');
      }
      throw new Error(msg);
    }
  }

  private async adminResetPasswordViaCallable(uid: string, pwd: string): Promise<void> {
    try {
      const callable = httpsCallable<{ uid: string; password: string }, { ok: boolean }>(
        this.functions,
        'adminResetUserPassword'
      );
      await this.run(() => callable({ uid, password: pwd }));
    } catch (error: any) {
      const code = error?.code ?? '';
      const message = error?.message ?? '';
      if (
        code === 'functions/not-found' ||
        code === 'functions/internal' ||
        code === 'functions/unavailable' ||
        message.includes('NOT_FOUND') ||
        message.includes('CORS') ||
        message === 'internal'
      ) {
        throw new Error(
          'Password reset service unavailable. Redeploy functions: firebase deploy --only functions -P dev. ' +
          'Or run: npm run reset:password -- <email> <newPassword>'
        );
      }
      if (code === 'functions/permission-denied') {
        throw new Error('Only administrators can reset passwords.');
      }
      if (code === 'functions/unauthenticated') {
        throw new Error('You must be logged in as admin to reset passwords.');
      }
      throw new Error(message || 'Could not reset password.');
    }
  }

  /** Resolve Firebase Auth UID for an existing account (sign-in on a throwaway app). */
  private async resolveAuthUidForExistingAccount(email: string, password: string): Promise<string> {
    const appName = `TempAuth_${Date.now()}`;
    const secondaryApp = initializeApp(environment.firebase, appName);
    const secondaryAuth = getAuth(secondaryApp);
    try {
      const cred = await signInWithEmailAndPassword(secondaryAuth, email.trim(), password);
      const uid = cred.user.uid;
      await fbSignOut(secondaryAuth);
      return uid;
    } finally {
      await deleteApp(secondaryApp);
    }
  }

  /** Create or sync one master test account in Auth + Firestore at users/{authUid}. */
  private async upsertMasterAccount(m: {
    email: string;
    password: string;
    displayName: string;
    role: UserRole;
    departmentIds?: readonly string[] | string[];
  }): Promise<string> {
    let uid: string;
    try {
      uid = await this.createFirebaseAuthUser(m.email, m.password);
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        uid = await this.resolveAuthUidForExistingAccount(m.email, m.password);
      } else {
        throw error;
      }
    }

    await this.run(() =>
      setDoc(
        doc(this.usersCollection, uid),
        {
          uid,
          email: m.email,
          displayName: m.displayName,
          role: m.role,
          isActive: true,
          createdAt: serverTimestamp(),
          lastLogin: null,
          departmentIds: [...(m.departmentIds ?? [])],
        },
        { merge: true }
      )
    );
    return uid;
  }

  /**
   * Create a new user document in Firestore
   */
  async createUser(uid: string, userData: any): Promise<void> {
    try {
      await this.run(() => setDoc(doc(this.usersCollection, uid), {
        uid,
        email: this.normalizeEmail(userData.email),
        displayName: userData.displayName,
        fullName: userData.fullName ?? userData.displayName ?? null,
        designation: userData.designation ?? null,
        profileImage: userData.profileImage ?? null,
        mobileNumber: userData.mobileNumber || null,
        employeeId: userData.employeeId || null,
        role: userData.role,
        departmentIds: userData.departmentIds || [],
        blockAccess: Array.isArray(userData.blockAccess) ? userData.blockAccess : [],
        remarks: userData.remarks || null,
        createdAt: serverTimestamp(),
        lastLogin: null,
        isActive: typeof userData.isActive === 'boolean' ? userData.isActive : true,
        assignedBy: userData.assignedBy || null,
        lastModifiedBy: userData.lastModifiedBy || null,
        assignmentAudit: userData.assignmentAudit || [],
        assignedDepartments: userData.assignedDepartments || [],
        assignedSchemes: userData.assignedSchemes || [],
        assignedApproverIds: userData.assignedApproverIds || [],
        assignedOperators: userData.assignedOperators || [],
        approvalLevel: userData.approvalLevel || null,
        approvalScope: userData.approvalScope || null,
        permissions: userData.permissions || {}
      }));
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Update an existing user document in Firestore
   */
  async updateUser(uid: string, userData: any): Promise<void> {
    try {
      const userDoc = doc(this.usersCollection, uid);

      // Build payload — coerce undefined → null / [] so Firestore accepts it.
      // Updates skip the `password` and any extra form-only fields entirely.
      const payload: Record<string, any> = {
        uid,
        email:                this.normalizeEmail(userData.email),
        displayName:          userData.displayName,
        fullName:             userData.fullName ?? userData.displayName ?? null,
        designation:          userData.designation ?? null,
        profileImage:         userData.profileImage ?? null,
        mobileNumber:         userData.mobileNumber ?? null,
        employeeId:           userData.employeeId ?? null,
        role:                 userData.role,
        departmentIds:        Array.isArray(userData.departmentIds) ? userData.departmentIds : [],
        blockAccess:          Array.isArray(userData.blockAccess) ? userData.blockAccess : [],
        remarks:              userData.remarks ?? null,
        updatedAt:            serverTimestamp(),
        lastModifiedBy:       userData.lastModifiedBy ?? null,
        assignedDepartments:  Array.isArray(userData.assignedDepartments) ? userData.assignedDepartments : [],
        assignedSchemes:      Array.isArray(userData.assignedSchemes)     ? userData.assignedSchemes     : [],
        assignedApproverIds:  Array.isArray(userData.assignedApproverIds) ? userData.assignedApproverIds : [],
        assignedOperators:    Array.isArray(userData.assignedOperators)   ? userData.assignedOperators   : [],
        approvalLevel:        userData.approvalLevel ?? null,
        approvalScope:        userData.approvalScope ?? null,
        permissions:          userData.permissions ?? {},
      };
      // Honour the form's isActive toggle if provided (preserves status flips).
      if (typeof userData.isActive === 'boolean') payload['isActive'] = userData.isActive;
      if (Array.isArray(userData.assignmentAudit)) payload['assignmentAudit'] = userData.assignmentAudit;

      // Strip any remaining `undefined` (Firestore rejects them outright).
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      await this.run(() => updateDoc(userDoc, payload));
    } catch (error: any) {
      // Surface the underlying code/message so we can see permission vs validation
      console.error('Error updating user:', {
        code:    error?.code,
        message: error?.message,
        raw:     error,
      });
      throw error;
    }
  }

  /**
   * Update user's last login timestamp
   */
  async updateLastLogin(uid: string): Promise<void> {
    try {
      await this.run(() => updateDoc(doc(this.usersCollection, uid), { lastLogin: serverTimestamp() }));
    } catch (error) {
      console.error('Error updating last login:', error);
      throw error;
    }
  }

  /**
   * Update user role
   */
  async updateUserRole(uid: string, role: UserRole): Promise<void> {
    try {
      await this.run(() => updateDoc(doc(this.usersCollection, uid), { role }));
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  /**
   * Deactivate user
   */
  async deactivateUser(uid: string): Promise<void> {
    try {
      await this.run(() => updateDoc(doc(this.usersCollection, uid), { isActive: false }));
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  }

  async activateUser(uid: string): Promise<void> {
    try {
      await this.run(() => updateDoc(doc(this.usersCollection, uid), { isActive: true }));
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  }

  /** Master test accounts — Auth + Firestore profile at users/{authUid}. */
  static readonly MASTER_ACCOUNTS = [
    {
      email: 'minister@nimbletech.co.in',
      password: 'Minister@2024!',
      displayName: 'Sri Suryabanshi Suraj',
      role: UserRole.MINISTER,
    },
    {
      email: 'osd@email.com',
      password: '123456',
      displayName: 'OSD',
      role: UserRole.OSD,
    },
    {
      email: 'admin@email.com',
      password: '123456',
      displayName: 'Administrator',
      role: UserRole.ADMIN,
    },
    {
      email: 'test_op@email.com',
      password: '123456',
      displayName: 'Test Operator',
      role: UserRole.OPERATOR,
      departmentIds: ['Works'],
    },
    {
      email: 'test_ap@email.com',
      password: '123456',
      displayName: 'Test Approver',
      role: UserRole.APPROVER,
      departmentIds: ['Works'],
    },
  ] as const;

  /**
   * Seed master accounts (Minister, OSD, Admin, Operator, Approver).
   * Syncs Firestore at users/{firebaseAuthUid} so security rules resolve roles correctly.
   */
  async seedMasterAccounts(): Promise<{ seeded: number; failed: string[] }> {
    console.log('--- SEEDING MASTER ACCOUNTS START ---');
    const failed: string[] = [];
    let seeded = 0;

    for (const m of UserService.MASTER_ACCOUNTS) {
      try {
        console.log(`Seeding account: ${m.email}`);
        await this.upsertMasterAccount(m);
        seeded++;
        console.log(`Account ${m.email} seeded successfully.`);
      } catch (error: any) {
        console.error(`Failed to seed ${m.email}:`, error);
        failed.push(m.email);
      }
    }

    return { seeded, failed };
  }

  /**
   * Helper to automatically recreate a specific test account if it was deleted.
   */
  async autoCreateTestAccount(email: string, password: string): Promise<string | null> {
    const match = UserService.MASTER_ACCOUNTS.find(
      m => m.email.toLowerCase() === email.toLowerCase() && m.password === password
    );
    if (!match) return null;

    console.log(`[Auto-Seed] Recreating deleted test account: ${email}`);
    return this.upsertMasterAccount(match);
  }

  /**
   * Get all users (for admin)
   */
  async getAllUsers(): Promise<User[]> {
    try {
      const querySnapshot = await this.run(() => getDocs(this.usersCollection));
      return querySnapshot.docs.map(d => {
        const data = d.data();
        return {
          uid: d.id,
          email: data['email'],
          displayName: data['displayName'],
          mobileNumber: data['mobileNumber'] || data['phoneNumber'],
          employeeId: data['employeeId'],
          role: this.normalizeRole(data['role'], d.id, data['email']),
          departmentIds: data['departmentIds'] || [],
          createdAt: data['createdAt']?.toDate() || new Date(),
          lastLogin: data['lastLogin']?.toDate(),
          isActive: data['isActive'] ?? true,
          remarks: data['remarks'],
          assignedDepartments: data['assignedDepartments'] || [],
          assignedSchemes: data['assignedSchemes'] || [],
          assignedApproverIds: data['assignedApproverIds'] || [],
          assignedOperators: data['assignedOperators'] || [],
          approvalLevel: data['approvalLevel'],
          approvalScope: data['approvalScope'],
          permissions: data['permissions'] || {}
        };
      });
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: UserRole): Promise<User[]> {
    try {
      const q = query(this.usersCollection, where('role', '==', role));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          email: data['email'],
          displayName: data['displayName'],
          mobileNumber: data['mobileNumber'] || data['phoneNumber'],
          employeeId: data['employeeId'],
          role: this.normalizeRole(data['role'], doc.id),
          departmentIds: data['departmentIds'] || [],
          createdAt: data['createdAt']?.toDate() || new Date(),
          lastLogin: data['lastLogin']?.toDate(),
          isActive: data['isActive'] ?? true,
          remarks: data['remarks']
        };
      });
    } catch (error) {
      console.error('Error fetching users by role:', error);
      throw error;
    }
  }

  /**
   * Delete user by ID
   */
  async deleteUser(uid: string): Promise<void> {
    try {
      await this.run(() => deleteDoc(doc(this.usersCollection, uid)));
      console.log('User deleted successfully:', uid);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }
}
